import React from 'react';
import { DollarSign, TrendingUp, TrendingDown } from 'lucide-react';

// Calculate total revenue from the trailing 8 weeks data
const calculateTrailing8WeeksRevenue = () => {
  // Using the same data structure as WeeklyProductivityChart (8 weeks)
  const weeklyRevenue = [8500, 9200, 9800, 9500, 10500, 8750, 11250, 10250];
  return weeklyRevenue.reduce((total, weekly) => total + weekly, 0);
};

const RevenueCard: React.FC = () => {
  const totalRevenue = calculateTrailing8WeeksRevenue();
  const averageWeeklyRevenue = Math.round(totalRevenue / 8);
  
  // Calculate growth percentage (comparing last 4 weeks vs first 4 weeks)
  const firstHalfRevenue = 8500 + 9200 + 9800 + 9500;
  const secondHalfRevenue = 10500 + 8750 + 11250 + 10250;
  const growthPercentage = Math.round(((secondHalfRevenue - firstHalfRevenue) / firstHalfRevenue) * 100);

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">My Revenue (8 Weeks)</h3>
        <DollarSign className="w-6 h-6 text-green-400" />
      </div>
      
      {/* Main Content - Flexible Layout */}
      <div className="flex-1 space-y-5">
        {/* Total Revenue */}
        <div className="text-center">
          <p className="text-3xl font-bold text-white">
            ${totalRevenue.toLocaleString()}
          </p>
          <p className="text-gray-400 text-sm">Total Revenue</p>
        </div>

        {/* Growth Indicator */}
        <div className="flex items-center justify-center space-x-2 py-2">
          {growthPercentage >= 0 ? (
            <TrendingUp className="w-5 h-5 text-green-400" />
          ) : (
            <TrendingDown className="w-5 h-5 text-red-400" />
          )}
          <span className={`text-lg font-semibold ${growthPercentage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {growthPercentage >= 0 ? '+' : ''}{growthPercentage}%
          </span>
          <span className="text-gray-400 text-sm">growth</span>
        </div>

        {/* Key Metrics Grid */}
        <div className="bg-gray-700 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-xl font-semibold text-white">
                ${averageWeeklyRevenue.toLocaleString()}
              </p>
              <p className="text-gray-400 text-xs">Weekly Avg</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-semibold text-white">
                ${Math.round(totalRevenue / 8 / 40 * 100) / 100}
              </p>
              <p className="text-gray-400 text-xs">Per Hour</p>
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="text-center">
            <p className="text-lg font-medium text-gray-300">
              {Math.round((totalRevenue / 8) / (totalRevenue / 8 / 40) * 10) / 10}h
            </p>
            <p className="text-gray-400 text-xs">Avg/Week</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-medium text-gray-300">
              ${(totalRevenue / 1000).toFixed(0)}K
            </p>
            <p className="text-gray-400 text-xs">Monthly Rate</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueCard;
